import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { ArrowsMergeIcon } from '@phosphor-icons/react';
import { useAggregateAPInvoiceLineItems } from '@/api/rpc/hooks/accounts-payable/useAggregateAPInvoiceLineItems';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/context/ToastContext';
import { formatCurrency } from '@/utils/formatters';
import {
  assessAggregation,
  rankAggregationTargets,
  toAggregatePayload,
} from './aggregateLineItemsForm';
import {
  AggregateChosenSummary,
  AggregateTargetList,
} from './AggregateTargetPicker';
import { useAggregationTargets } from './useAggregationTargets';

interface AggregateLineItemSource {
  id: string;
  lineNumber: number;
  description?: string | null;
  totalPrice?: string | null;
}

interface AggregateLineItemsDialogProps {
  invoiceId: string;
  supplierId?: string | null;
  /** Every line of the invoice; the related-lines summary maps matches to them. */
  allLineItems: { id: string; lineNumber: number }[];
  /** The 2 or more lines to roll up. */
  selectedLineItems: AggregateLineItemSource[];
  currency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAggregated: () => void;
}

/**
 * Match several invoice lines to ONE related record — a charge or an order
 * line — as an aggregated match, for a bill the supplier split across lines
 * that the ERP carries as one record ($3,850 + $150 against a $4,000 freight
 * charge).
 */
export function AggregateLineItemsDialog(props: AggregateLineItemsDialogProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        {open && (
          <AggregateLineItemsBody
            {...props}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AggregateLineItemsBody({
  invoiceId,
  supplierId,
  allLineItems,
  selectedLineItems,
  currency,
  onAggregated,
  onClose,
}: AggregateLineItemsDialogProps & { onClose: () => void }) {
  const gt = useGT();
  const { showToast } = useToast();
  const aggregate = useAggregateAPInvoiceLineItems();
  const [targetKey, setTargetKey] = useState<string | null>(null);
  const [commonDescription, setCommonDescription] = useState('');

  const { targets, hasDocuments, isLoading } = useAggregationTargets({
    invoiceId,
    supplierId,
    allLineItems,
    selectedLineNumbers: new Set(
      selectedLineItems.map((line) => line.lineNumber),
    ),
  });
  const rankedTargets = rankAggregationTargets(
    targets,
    selectedLineItems.map((line) => line.totalPrice),
  );
  const chosen =
    rankedTargets.find((option) => option.key === targetKey) ?? null;
  const assessment = assessAggregation(
    selectedLineItems.map((line) => line.totalPrice),
    chosen?.amount ?? null,
  );
  const money = (value: string | null | undefined) =>
    formatCurrency(value == null ? null : Number(value), { currency });

  function handleSubmit() {
    if (!chosen) return;
    aggregate.mutate(
      toAggregatePayload({
        invoiceId,
        lineItemIds: selectedLineItems.map((line) => line.id),
        target: chosen.target,
        commonDescription,
      }),
      {
        onSuccess: ({ lineItemIds }) => {
          showToast({
            message: gt('{count} lines aggregated into one match', {
              count: lineItemIds.length,
              context: 'toast after aggregating AP invoice lines',
            }),
            color: 'success',
          });
          onAggregated();
          onClose();
        },
        onError: (error) => {
          showToast({ message: error.message, color: 'error' });
        },
      },
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title: match several AP invoice lines to one record">
            Aggregate <Var>{selectedLineItems.length}</Var> Lines
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="aggregate lines dialog description">
            Lines{' '}
            <Var>{selectedLineItems.map((l) => l.lineNumber).join(', ')}</Var>{' '}
            add up to <Var>{money(assessment.selectedTotal)}</Var>. Pick the one
            charge or order line they are billing together. Any existing match
            on these lines is replaced.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[40vh] overflow-y-auto">
        <AggregateTargetList
          targets={rankedTargets}
          isLoading={isLoading}
          hasDocuments={hasDocuments}
          value={targetKey}
          onValueChange={setTargetKey}
          money={money}
        />
      </div>

      {chosen && (
        <AggregateChosenSummary
          chosen={chosen}
          assessment={assessment}
          money={money}
        />
      )}

      <div className="space-y-1">
        <Label htmlFor="aggregate-common-description">
          <T context="aggregate dialog: optional label for the group">
            Label (optional)
          </T>
        </Label>
        <Input
          id="aggregate-common-description"
          value={commonDescription}
          maxLength={200}
          placeholder={gt('e.g. Freight', {
            context: 'aggregate dialog: label placeholder',
          })}
          onChange={(e) => setCommonDescription(e.target.value)}
          className="h-8"
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T context="aggregate dialog: cancel button">Cancel</T>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!chosen || aggregate.isPending}
        >
          <ArrowsMergeIcon className="h-4 w-4 mr-1" />
          <T context="aggregate dialog: confirm button">Aggregate</T>
        </Button>
      </DialogFooter>
    </>
  );
}
